import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Download, Upload, Send, CheckCircle2, Circle } from "lucide-react";

export default function MyClientDetailSheet({ clientId, myRole, open, onOpenChange }) {
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [files, setFiles] = useState([]);
  const [team, setTeam] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [comments, setComments] = useState([]);
  const [newMessage, setNewMessage] = useState("");

  const load = async () => {
    if (!clientId) return;
    const [c, tk, inv, fl, tm, cl, cm] = await Promise.all([
      api.get(`/clients/${clientId}`),
      api.get(`/tasks`),
      api.get(`/clients/${clientId}/invoices`),
      api.get(`/clients/${clientId}/files`),
      api.get(`/clients/${clientId}/team`),
      api.get(`/clients/${clientId}/checklists`),
      api.get(`/clients/${clientId}/comments`),
    ]);
    setClient(c.data);
    setTasks(tk.data.filter((t) => t.client_id === clientId));
    setInvoices(inv.data);
    setFiles(fl.data);
    setTeam(tm.data);
    setChecklists(cl.data);
    setComments(cm.data);
  };

  useEffect(() => {
    if (open && clientId) load();
  }, [open, clientId]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/clients/${clientId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post(`/clients/${clientId}/comments`, { message: newMessage.trim() });
      setNewMessage("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleItem = async (checklistId, itemId) => {
    try {
      await api.put(`/clients/${clientId}/checklists/${checklistId}/items/${itemId}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!client) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="my-client-detail-sheet">
        <SheetHeader>
          <div className="flex items-center justify-between pr-8">
            <SheetTitle className="font-heading" data-testid="my-client-sheet-name">{client.company_name}</SheetTitle>
            <span className="text-xs font-medium text-[#00823B] bg-[#76C121]/10 px-2 py-1 rounded-md">{myRole}</span>
          </div>
          <p className="text-sm text-[#4B5563]">{client.engagement_scope}</p>
        </SheetHeader>

        <Tabs defaultValue="tasks" className="mt-6">
          <TabsList className="flex flex-wrap w-full gap-1 h-auto">
            <TabsTrigger value="tasks" data-testid="my-client-tab-tasks">Tasks</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="my-client-tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="checklists" data-testid="my-client-tab-checklists">Checklists</TabsTrigger>
            <TabsTrigger value="files" data-testid="my-client-tab-files">Files</TabsTrigger>
            <TabsTrigger value="team" data-testid="my-client-tab-team">Team</TabsTrigger>
            <TabsTrigger value="messages" data-testid="my-client-tab-messages">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="mt-4 space-y-2">
            {tasks.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No tasks logged for this client.</p>}
            {tasks.map((t) => (
              <div key={t.id} data-testid={`my-client-task-${t.id}`} className="p-3 border border-[#E2E8F0] rounded-md flex items-center justify-between">
                <p className="text-sm font-medium text-[#1C1C1E]">{t.title}</p>
                <StatusBadge value={t.status} />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="invoices" className="mt-4 space-y-2">
            {invoices.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No invoices yet.</p>}
            {invoices.map((inv) => (
              <div key={inv.id} data-testid={`my-client-invoice-${inv.id}`} className="p-3 border border-[#E2E8F0] rounded-md flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{inv.invoice_number}</p>
                  <p className="text-xs text-[#4B5563]">₹{Number(inv.amount).toLocaleString("en-IN")} · Due {inv.due_date}</p>
                </div>
                <StatusBadge value={inv.status} />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="checklists" className="mt-4 space-y-3">
            {checklists.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No checklists assigned.</p>}
            {checklists.map((cl) => (
              <div key={cl.id} data-testid={`my-client-checklist-${cl.id}`} className="border border-[#E2E8F0] rounded-md p-3">
                <p className="text-sm font-semibold text-[#1C1C1E] mb-2">{cl.template_name}</p>
                <div className="space-y-1.5">
                  {cl.items.map((it) => (
                    <button key={it.id} data-testid={`my-client-checklist-item-${it.id}`} onClick={() => toggleItem(cl.id, it.id)} className="flex items-center gap-2 text-sm w-full text-left">
                      {it.completed ? <CheckCircle2 className="h-4 w-4 text-[#00823B] shrink-0" /> : <Circle className="h-4 w-4 text-[#4B5563] shrink-0" />}
                      <span className={it.completed ? "text-[#4B5563] line-through" : "text-[#1C1C1E]"}>{it.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="files" className="mt-4 space-y-3">
            <label className="inline-block">
              <Button size="sm" asChild data-testid="my-client-upload-file-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white cursor-pointer">
                <span><Upload className="h-3.5 w-3.5 mr-1" /> Upload File</span>
              </Button>
              <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} />
            </label>
            {files.map((f) => (
              <div key={f.id} data-testid={`my-client-file-${f.id}`} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md">
                <p className="text-sm text-[#1C1C1E] truncate">{f.original_filename}</p>
                <div className="flex items-center gap-2.5">
                  <a href={fileUrl(f.file_path)} target="_blank" rel="noreferrer" title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                  <a href={downloadUrl(f.file_path, f.original_filename)} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="team" className="mt-4 space-y-2">
            {team.map((t) => (
              <div key={t.id} data-testid={`my-client-team-${t.id}`} className="p-3 border border-[#E2E8F0] rounded-md flex items-center justify-between">
                <p className="text-sm font-medium">{t.employee_name}</p>
                <span className="text-xs text-[#00823B]">{t.role_label}</span>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="messages" className="mt-4">
            <div className="border border-[#E2E8F0] rounded-md p-4 space-y-3 max-h-[320px] overflow-y-auto">
              {comments.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No messages yet.</p>}
              {comments.map((c) => (
                <div key={c.id} data-testid={`my-client-comment-${c.id}`} className={`max-w-[80%] ${c.author_role === "client" ? "" : "ml-auto text-right"}`}>
                  <div className={`inline-block px-3 py-2 rounded-lg text-sm ${c.author_role === "client" ? "bg-[#F8F9FA]" : "bg-[#76C121]/10"}`}>{c.message}</div>
                  <p className="text-[10px] text-[#4B5563] mt-1">{c.author_name} · {new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Textarea data-testid="my-client-message-input" placeholder="Message the client..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} rows={1} className="resize-none" />
              <Button data-testid="my-client-send-message-button" onClick={sendMessage} className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
